import {
  type CarrierContactFragment,
  type CarrierContactType,
  useAddCarrierContactToAppetiteProductMutation,
} from "@qw/graphql";
import {
  Button,
  ButtonGroup,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Group,
  Icon,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useToast,
} from "@qw/ui";
import React, { useState } from "react";

import { CarrierContactForm } from "./carrier-contact-form";
import { CarrierContactSelector } from "./carrier-contact-selector";

interface Props {
  type: CarrierContactType;
  contacts: CarrierContactFragment[];
  appetiteProductId: string;
}

export const CreateCarrierContactButton: React.FC<Props> = ({ type, appetiteProductId, contacts }) => {
  const { toast } = useToast();
  const [open, setOpen] = React.useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedContact, setSelectedContact] = useState<CarrierContactFragment | null>(null);
  const [addCarrierContact, { loading }] = useAddCarrierContactToAppetiteProductMutation();

  const handleCreateComplete = () => {
    setOpen(false);
    setTimeout(() => {
      setShowForm(false);
      setSelectedContact(null);
    }, 300);
  };

  const handleSelected = (selected: CarrierContactFragment | null) => {
    if (selected?.id === "new") {
      setShowForm(true);
      setSelectedContact(null);
    } else {
      setSelectedContact(selected);
    }
  };

  const handleSubmit = () => {
    if (selectedContact) {
      void addCarrierContact({
        variables: { input: { id: selectedContact.id, appetiteProductId, type } },
        onCompleted: () => {
          toast({ title: "Contact added" });
          setOpen(false);
          setSelectedContact(null);
        },
        refetchQueries: ["Product"],
      });
    }
  };

  const handleCancel = () => {
    setOpen(false);
    setTimeout(() => {
      setShowForm(false);
      setSelectedContact(null);
    }, 300);
  };

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      setTimeout(() => {
        setShowForm(false);
        setSelectedContact(null);
      }, 300);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <Tooltip>
        <TooltipTrigger asChild>
          <DialogTrigger asChild>
            <Button variant="ghost" size="xs" display="icon" onClick={() => setOpen(true)}>
              <Icon icon="add_2" />
            </Button>
          </DialogTrigger>
        </TooltipTrigger>
        <TooltipContent>Create / Add Contact</TooltipContent>
      </Tooltip>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{showForm ? "Create" : "Add"} Carrier Contact</DialogTitle>
        </DialogHeader>

        {showForm ? (
          <CarrierContactForm appetiteProductId={appetiteProductId} type={type} onCompleted={handleCreateComplete} />
        ) : (
          <Group>
            <CarrierContactSelector setSelected={handleSelected} excludedContactIds={contacts.map((c) => c.id)} />
            <ButtonGroup>
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={!selectedContact || loading}>
                Add Contact
              </Button>
            </ButtonGroup>
          </Group>
        )}
      </DialogContent>
    </Dialog>
  );
};
